import { useEffect, useRef, useState } from "react";
import { getData } from "../../Services/getData";
import { Vocabulary } from "../../Utils/Vocabulary";
import styles from "../../Styles/previewPDF.module.css";
import {
  CircularProgress,
  DialogContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { LazyDocumentSlotProps, PreviewDocumentProps } from "../../Utils/Types";
import { pdfjs } from "react-pdf";
import LazyPdfDocumentGroup from "./LazyPdfDocumentGroup";
import ZoomControls from "./ZoomControls";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const DOCUMENT_LOAD_MARGIN = "600px 0px 600px 0px";
const SCALE_STEP = 0.25;
const MIN_SCALE = 0.3;
const MAX_SCALE = 2;

/**
 * Defers mounting a whole PDF document until it scrolls near the viewport,
 * so a module with several documents doesn't parse/render all of them upfront.
 */
function LazyDocumentSlot({
  fileUrl,
  scale,
  docIndex,
  isLoaded,
  setLoadedDocIndices,
}: LazyDocumentSlotProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoaded) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoadedDocIndices((prev) =>
            prev.has(docIndex) ? prev : new Set(prev).add(docIndex)
          );
        }
      },
      { rootMargin: DOCUMENT_LOAD_MARGIN }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isLoaded, docIndex, setLoadedDocIndices]);

  if (!isLoaded) {
    return (
      <div ref={ref} className={styles.loadingWrapper}>
        <CircularProgress color="secondary" />
      </div>
    );
  }

  return <LazyPdfDocumentGroup fileUrl={fileUrl} scale={scale} />;
}

function PreviewPDFDoc(props: PreviewDocumentProps) {
  const { urls, htmlContent } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [scale, setScale] = useState(isMobile ? 0.5 : 1.0);
  const [state, setState] = useState({
    templateLoaded: 0,
  });
  const [content, setContent] = useState(htmlContent);
  const [loadedDocIndices, setLoadedDocIndices] = useState<Set<number>>(
    new Set([0])
  );

  useEffect(() => {
    if (urls?.length) {
      const url = urls[0];
      getData(url).then((response: any) => {
        if (response?.errors) return;
        setState((prev) => ({ ...prev, templateLoaded: 1 }));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urls]);

  useEffect(() => {
    if (!content) {
      setContent(htmlContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlContent]);

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + SCALE_STEP, MAX_SCALE));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - SCALE_STEP, MIN_SCALE));
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <DialogContent className={styles.dialogContent}>
        {urls?.length ? (
          !state.templateLoaded ? (
            <div className={styles.loadingWrapper}>
              <CircularProgress color="secondary" />
            </div>
          ) : (
            urls.map((pdfUrl, idx) => (
              <LazyDocumentSlot
                key={idx}
                fileUrl={pdfUrl}
                scale={scale}
                docIndex={idx}
                isLoaded={loadedDocIndices.has(idx)}
                setLoadedDocIndices={setLoadedDocIndices}
              />
            ))
          )
        ) : (
          Vocabulary.loading
        )}
      </DialogContent>
      {!!state.templateLoaded && (
        <ZoomControls
          scale={scale}
          minScale={MIN_SCALE}
          maxScale={MAX_SCALE}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
        />
      )}
    </div>
  );
}

export default PreviewPDFDoc;
